<template>
  <Expandable toggle-on-click @toggle="onToggle">
    <template #header="{ expanded }">
      <div class="workflow-history__divider"></div>
      <div class="workflow-history__header">
        <img :src="historyIconPath" width="16" height="16" />
        <span class="workflow-history__header-title">
          {{ historyTitlePrefix }}{{ statusTitle }}
        </span>
        <span
          v-if="item.completed_on"
          :title="completedDate"
          class="workflow-history__header-date"
        >
          {{ humanCompletedDate }}
        </span>
        <Icon
          :icon="
            expanded ? 'iconoir-nav-arrow-down' : 'iconoir-nav-arrow-right'
          "
          type="secondary"
        />
      </div>
      <div v-if="triggeredByName" class="workflow-history__started-by">
        {{ $t('historySidePanel.startedBy', { name: triggeredByName }) }}
      </div>
    </template>

    <template #default>
      <template v-if="item.status !== 'started'">
        <div
          v-if="nodeHistoriesEntry === null"
          class="workflow-history__message"
        >
          <div class="loading"></div>
        </div>
        <template v-else>
          <NodeHistory
            v-for="nh in rootNodeHistories"
            :key="nh.id"
            :workflow-history-id="item.id"
            :node-history="nh"
            :child-node-histories-by-parent="childNodeHistoriesByParent"
            :error-descendant-node-ids="errorDescendantNodeIds"
            :pass-info-by-history-id="passInfoByHistoryId"
            :depth="0"
          />
          <div v-if="workflowLevelMessage" class="workflow-history__message">
            {{ workflowLevelMessage }}
          </div>
        </template>
        <div class="workflow-history__run-time">
          {{ totalRunTimeMessage }}
        </div>
      </template>
      <template v-else>
        <div class="workflow-history__run-time">
          {{ totalRunTimeMessage }}
        </div>
        <div
          v-if="item.cancellation_requested_on"
          class="workflow-history__cancel"
        >
          {{ $t('historySidePanel.cancelling') }}
        </div>
        <div v-else-if="canCancel" class="workflow-history__cancel">
          <a
            role="button"
            class="workflow-history__cancel-link"
            :class="{ 'workflow-history__cancel-link--disabled': cancelling }"
            @click="cancelRun()"
          >
            {{ $t('historySidePanel.cancelRun') }}
          </a>
        </div>
      </template>
      <div
        v-if="workflowHistoryComponents.length"
        class="workflow-history__components"
      >
        <component
          :is="component"
          v-for="(component, index) in workflowHistoryComponents"
          :key="index"
          :workflow-history="item"
        />
      </div>
    </template>
  </Expandable>
</template>

<script setup>
import { useStore } from 'vuex'
import moment from '@baserow/modules/core/moment'
import { getUserTimeZone } from '@baserow/modules/core/utils/date'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { ResponseErrorMessage } from '@baserow/modules/core/plugins/clientHandler'

import historySuccessIcon from '@baserow/modules/core/assets/images/history-success.svg?url'
import historyFailedIcon from '@baserow/modules/core/assets/images/history-failed.svg?url'
import historyDisabledIcon from '@baserow/modules/core/assets/images/history-disabled.svg?url'
import NodeHistory from '@baserow/modules/automation/components/workflow/sidePanels/NodeHistory.vue'
import { getCollaboratorName } from '@baserow/modules/core/utils/collaborator'

const app = useNuxtApp()
const store = useStore()

const workspace = inject('workspace')
const workflow = inject('workflow')

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})

const now = ref(new Date())
let timer = null

const cancelling = ref(false)

/**
 * Whoever can update the workflow can cancel its runs.
 */
const canCancel = computed(() =>
  app.$hasPermission(
    'automation.workflow.update',
    workflow.value,
    workspace.value.id
  )
)

/**
 * Cancellation is cooperative: the backend records the request and the run
 * stops before its next node is dispatched. Until then the entry shows
 * "Cancelling..." and keeps its running timer. If the run finishes before the
 * cancellation takes effect, the backend answers that it is not running
 * anymore; the refetch then simply shows the terminal state. If somebody else
 * requested the cancellation first, the backend refuses this request: the
 * refetch shows the entry as cancelling and a toast makes clear that the
 * request isn't this user's, so the attribution can't be misread.
 */
const cancelRun = async () => {
  if (cancelling.value) return
  cancelling.value = true
  try {
    await store.dispatch('automationHistory/cancelWorkflowRun', {
      workflowId: workflow.value.id,
      workflowHistoryId: props.item.id,
    })
  } catch (error) {
    const code = error.handler?.code
    if (code === 'ERROR_AUTOMATION_WORKFLOW_HISTORY_NOT_RUNNING') {
      // Photo-finish: the run resolved first, the refetch shows its outcome.
    } else if (
      code ===
      'ERROR_AUTOMATION_WORKFLOW_HISTORY_CANCELLATION_ALREADY_REQUESTED'
    ) {
      error.handler.notifyIf(
        'automationWorkflow',
        new ResponseErrorMessage(
          app.$i18n.t('historySidePanel.cancellationAlreadyRequestedTitle'),
          app.$i18n.t('historySidePanel.cancellationAlreadyRequested')
        )
      )
    } else {
      notifyIf(error, 'automationWorkflow')
    }
  } finally {
    cancelling.value = false
  }
}

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

watch(
  () => props.item.status,
  (status) => {
    if (status === 'started') {
      timer = setInterval(() => {
        now.value = new Date()
      }, 1000)
    } else if (timer) {
      clearInterval(timer)
      timer = null
      // When the workflow run completes, if the WorkflowHistory is already
      // expanded, we should fetch the node histories.
      store.dispatch('automationHistory/fetchNodeHistories', {
        workflowHistoryId: props.item.id,
      })
    }
  },
  { immediate: true }
)

const onToggle = () => {
  if (props.item.status === 'started') return
  store.dispatch('automationHistory/fetchNodeHistories', {
    workflowHistoryId: props.item.id,
  })
}

const nodeHistoriesEntry = computed(() =>
  store.getters['automationHistory/getNodeHistories'](props.item.id)
)

// A user's name comes from the workspace store so a rename shows; any other
// subject, or a user who left, falls back to the name stored on the run.
const triggeredByName = computed(() => {
  const triggeredBy = props.item.triggered_by
  if (!triggeredBy) return ''
  if (triggeredBy.type !== 'auth.User') return triggeredBy.name
  return getCollaboratorName(triggeredBy, store)
})

const statusTitle = computed(() => {
  switch (props.item.status) {
    case 'success':
      return app.$i18n.t('historySidePanel.statusSuccess')
    case 'error':
      return app.$i18n.t('historySidePanel.statusError')
    case 'started':
      return app.$i18n.t('historySidePanel.statusStarted')
    case 'cancelled':
      return app.$i18n.t('historySidePanel.statusCancelled')
    default:
      return app.$i18n.t('historySidePanel.statusDisabled')
  }
})

const completedDate = computed(() => {
  return moment
    .utc(props.item.completed_on)
    .tz(getUserTimeZone())
    .format('YYYY-MM-DD HH:mm:ss')
})

const humanCompletedDate = computed(() => {
  return moment.utc(props.item.completed_on).tz(getUserTimeZone()).fromNow()
})

const workflowHistoryComponents = computed(() => {
  return Object.values(app.$registry.getAll('plugin')).reduce(
    (components, plugin) =>
      components.concat(plugin.getWorkflowHistoryComponents(props.item)),
    []
  )
})

const historyTitlePrefix = computed(() => {
  return props.item.is_test_run === true
    ? `[${app.$i18n.t('historySidePanel.testRun')}] `
    : ''
})

/**
 * Return an array of root nodes, e.g. nodes that do not have a parent node.
 * They are rendered directly by WorkflowHistory. All other nodes are rendered
 * recursively by NodeHistory.
 */
const rootNodeHistories = computed(() => {
  const roots = []
  for (const nh of nodeHistoriesEntry.value ?? []) {
    if (nh.parent_node_id == null) {
      roots.push(nh)
    }
  }
  return roots
})

/**
 * Return an object where keys are parent node IDs and values are an array
 * of child node histories for that node.
 *
 * This is used to determine if a node has children, as well as the number of
 * runs for a collection node.
 */
const childNodeHistoriesByParent = computed(() => {
  const _childNodeHistoriesByParent = {}
  for (const nodeHistory of nodeHistoriesEntry.value ?? []) {
    if (nodeHistory.parent_node_id != null) {
      const parent = nodeHistory.parent_node_id
      if (!_childNodeHistoriesByParent[parent])
        _childNodeHistoriesByParent[parent] = []
      _childNodeHistoriesByParent[parent].push(nodeHistory)
    }
  }
  return _childNodeHistoriesByParent
})

/**
 * Maps each node history id to its pass number and the total number of passes
 * for that node within this run. A node runs more than once when a "Go to node"
 * jump loops execution back to it. Passes are scoped by (node, iteration_path)
 * so iterator iterations aren't conflated with goto loop passes, and counted in
 * chronological order (the histories are returned ordered by started_on, id).
 *
 * A node whose error policy retries it writes one history per attempt. Only the
 * first attempt opens a new pass; the retries that follow it belong to the same
 * pass, so they take its number and don't inflate the total.
 */
const passInfoByHistoryId = computed(() => {
  const items = nodeHistoriesEntry.value ?? []
  const isFirstAttempt = (nh) => (nh.attempt ?? 1) === 1

  const totals = {}
  for (const nh of items) {
    if (!isFirstAttempt(nh)) continue
    const key = `${nh.node}|${nh.iteration_path}`
    totals[key] = (totals[key] || 0) + 1
  }

  const counters = {}
  const result = {}
  for (const nh of items) {
    const key = `${nh.node}|${nh.iteration_path}`
    if (isFirstAttempt(nh)) counters[key] = (counters[key] || 0) + 1
    result[nh.id] = { pass: counters[key] || 1, total: totals[key] || 1 }
  }
  return result
})

/**
 * Precomputed set of parent node IDs that have at least one errored
 * node history in their descendant subtree.
 */
const errorDescendantNodeIds = computed(() => {
  const items = nodeHistoriesEntry.value ?? []
  const nodesParents = new Map()
  for (const nh of items) {
    if (!nodesParents.has(nh.node)) {
      nodesParents.set(nh.node, nh.parent_node_id)
    }
  }

  const parentsWithErroredChild = new Set()
  for (const nh of items) {
    if (nh.status !== 'error') continue

    // We exclude the node itself so that different iterations of the same
    // node aren't tied to each other's own error status.
    let current = nodesParents.get(nh.node)

    // If a parent has already been marked as having an errored child, we
    // stop early to avoid unnecessary walks - since all of its ancestors
    // have already been marked.
    while (current != null && !parentsWithErroredChild.has(current)) {
      parentsWithErroredChild.add(current)
      current = nodesParents.get(current)
    }
  }
  return parentsWithErroredChild
})

/**
 * Surfaces a workflow-history message that isn't reflected on any node history.
 *
 * Today every workflow-level error raised after dispatch begins is also written
 * to the corresponding node history (so NodeHistory renders it and this returns
 * null to avoid duplication). The remaining messages are pre-dispatch failures
 * (e.g. rate limited, disabled after too many errors, before-run errors) which
 * have no node histories at all. This computed is therefore also a forward-safe
 * guard: should we ever log a workflow-level error after dispatch that no node
 * carries, its message still gets surfaced here instead of being swallowed.
 */
const workflowLevelMessage = computed(() => {
  if (!props.item.message) return null
  const hasErroredNode = (nodeHistoriesEntry.value ?? []).some(
    (nh) => nh.status === 'error'
  )
  return hasErroredNode ? null : props.item.message
})

const historyIconPath = computed(() => {
  switch (props.item.status) {
    case 'success':
      return historySuccessIcon
    case 'error':
      return historyFailedIcon
    default:
      return historyDisabledIcon
  }
})

const totalRunTimeMessage = computed(() => {
  const start = new Date(props.item.started_on)

  if (props.item.status === 'started') {
    const deltaMs = now.value - start
    const deltaSeconds = deltaMs / 1000
    return app.$i18n.t('historySidePanel.running', {
      at: Math.floor(deltaSeconds),
    })
  } else {
    const end = new Date(props.item.completed_on)

    const deltaMs = end - start
    if (deltaMs < 1000) {
      return app.$i18n.t('historySidePanel.completedInLessThanSecond')
    } else {
      const deltaSeconds = deltaMs / 1000
      return app.$i18n.t('historySidePanel.completedInSeconds', {
        s: Math.floor(deltaSeconds),
      })
    }
  }
})
</script>
